import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Calendar, Mail } from "lucide-react";
import { api } from "@/lib/api";

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState(sessionId ? "polling" : "ready");
    const [result, setResult] = useState(null);
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) return;
        let timer;

        const poll = async () => {
            try {
                const { data } = await api.get(
                    `/checkout-session/${sessionId}`,
                );
                setResult(data);
                if (
                    data.payment_status === "paid" ||
                    data.status === "complete"
                ) {
                    setState("paid");
                    return;
                }
                attempts.current += 1;
                if (attempts.current >= 8) {
                    setState("ready");
                    return;
                }
                timer = setTimeout(poll, 2000);
            } catch {
                attempts.current += 1;
                if (attempts.current >= 8) {
                    setState("ready");
                    return;
                }
                timer = setTimeout(poll, 2000);
            }
        };

        poll();
        return () => clearTimeout(timer);
    }, [sessionId]);

    return (
        <div
            className="bg-cream min-h-[80vh] flex items-center justify-center px-6 py-16"
            data-testid="checkout-success-page"
        >
            <div className="max-w-xl w-full text-center">
                {state === "polling" && (
                    <div data-testid="checkout-success-polling">
                        <Loader2
                            size={48}
                            className="mx-auto text-gold animate-spin"
                        />
                        <h1 className="font-serif text-4xl text-forest mt-6">
                            Confirming your payment…
                        </h1>
                        <p className="mt-4 text-brown">
                            One moment while we finalize things with Stripe.
                        </p>
                    </div>
                )}

                {(state === "paid" || state === "ready") && (
                    <div data-testid="checkout-success-paid">
                        <CheckCircle2
                            size={56}
                            className="mx-auto text-gold"
                        />
                        <span className="divider-rule text-brown mt-8">
                            Thank you
                        </span>
                        <h1 className="font-serif text-5xl md:text-6xl text-forest mt-4 leading-[1.02]">
                            Order{" "}
                            <span className="italic text-gold">confirmed.</span>
                        </h1>
                        <p className="mt-5 text-brown leading-relaxed">
                            Thank you — your order is confirmed. We'll be in
                            touch with next steps and a receipt by email.
                        </p>

                        {result && result.amount_total > 0 && (
                            <div className="mt-8 p-6 bg-cream-warm rounded-sm border border-forest/10 inline-block text-left">
                                <p className="text-[0.65rem] tracking-[0.3em] uppercase text-brown">
                                    Amount Paid
                                </p>
                                <p className="font-serif text-3xl text-forest mt-1">
                                    ${Number(result.amount_total).toFixed(2)}{" "}
                                    <span className="text-sm text-brown">
                                        {result.currency?.toUpperCase()}
                                    </span>
                                </p>
                                {result.customer_email && (
                                    <p className="mt-2 text-xs text-brown inline-flex items-center gap-1.5">
                                        <Mail size={12} />
                                        Receipt sent to {result.customer_email}
                                    </p>
                                )}
                            </div>
                        )}

                        <div
                            className="mt-8 p-6 bg-forest text-cream rounded-sm text-left"
                            data-testid="checkout-success-next-steps"
                        >
                            <p className="text-[0.65rem] tracking-[0.3em] uppercase text-gold mb-3">
                                What happens next
                            </p>
                            <ol className="space-y-3 text-sm text-cream/85">
                                <li className="flex items-start gap-3">
                                    <span className="text-gold font-serif">
                                        01
                                    </span>
                                    A confirmation email is on its way from
                                    Stripe with your receipt.
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-gold font-serif">
                                        02
                                    </span>
                                    Our team will reach out within one business
                                    day to schedule your 60-minute call.
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-gold font-serif">
                                        03
                                    </span>
                                    Come with your questions — we'll come with
                                    a plan.
                                </li>
                            </ol>
                        </div>

                        <div className="mt-10 flex flex-wrap gap-3 justify-center">
                            <Link
                                to="/"
                                className="btn-pill btn-primary"
                                data-testid="checkout-success-home-btn"
                            >
                                Back to Home
                            </Link>
                            <Link
                                to="/shop"
                                className="btn-pill btn-ghost"
                                data-testid="checkout-success-shop-btn"
                            >
                                <Calendar size={14} /> Explore the Shop
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
